import { useEffect, useState } from "react";

import type { IQuerySuggestion } from "../types/api/algolia";
import searchClient, { getAlgoliaIndex, getQuerySuggestionsIndex } from "./client";

interface IQuerySuggestionHit {
  query?: string;
  _highlightResult?: { query?: { value?: string } };
  [sourceIndex: string]: unknown;
}

/**
 * 입력 중 자동완성용 추천 검색어.
 *
 * Query Suggestions 인덱스를 조회한다. 상품이 0건인 추천은 눌러도 빈 화면이 되므로
 * exact_nb_hits 로 걸러낸다. 검색어가 비어 있으면 조회하지 않는다.
 *
 * sortByHits 를 켜면 인덱스 순서 대신 상품 결과가 많은 순으로 보여 준다.
 * (facet 으로 만든 추천은 인기도가 없어 인덱스 순서가 알파벳 역순이 되기 때문)
 */
export default function useAlgoliaQuerySuggestions(
  query: string,
  options?: { onError?: (error: unknown) => void; sortByHits?: boolean },
): IQuerySuggestion[] {
  const [suggestions, setSuggestions] = useState<IQuerySuggestion[]>([]);
  const onError = options?.onError;
  const sortByHits = options?.sortByHits ?? false;

  useEffect(() => {
    const keyword = query.trim();

    if (!keyword) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;

    // 인덱스명은 스토어(도메인)마다 달라서 모듈 로드 시점이 아니라 조회할 때 읽는다.
    const suggestionsIndex = getQuerySuggestionsIndex();
    const sourceIndex = getAlgoliaIndex();

    const timer = setTimeout(() => {
      searchClient
        .search([
          {
            indexName: suggestionsIndex,
            params: {
              query: keyword,
              // 결과 수로 다시 정렬할 때는 후보를 넉넉히 받아 상위만 남긴다
              hitsPerPage: sortByHits ? 100 : 8,
              // 인덱스 기본값은 <em>. 커넥터가 설정을 덮어쓸 수 있어 쿼리에서 지정한다.
              highlightPreTag: "<b>",
              highlightPostTag: "</b>",
            },
          },
        ])
        .then((response) => {
          if (cancelled) return;

          const hits = (response as { results: { hits?: IQuerySuggestionHit[] }[] }).results[0]?.hits ?? [];

          const hitCount = (hit: IQuerySuggestionHit) => (hit[sourceIndex] as { exact_nb_hits?: number } | undefined)?.exact_nb_hits ?? 0;

          const visible = hits
            .filter((hit) => hitCount(hit) > 0)
            .filter((hit): hit is IQuerySuggestionHit & { query: string } => Boolean(hit.query));

          // 결과 수가 같으면 인덱스 순서를 유지한다(Array.prototype.sort 는 안정 정렬).
          const ordered = sortByHits ? [...visible].sort((a, b) => hitCount(b) - hitCount(a)).slice(0, 8) : visible;

          setSuggestions(
            ordered.map((hit) => ({
              query: hit.query,
              highlighted: hit._highlightResult?.query?.value ?? hit.query,
            })),
          );
        })
        .catch((error: unknown) => {
          // 자동완성이 실패해도 검색 자체는 계속돼야 하므로 throw 하지 않는다.
          onError?.(error);
          if (!cancelled) setSuggestions([]);
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, sortByHits]);

  return suggestions;
}
